import {
  Overlay,
  OverlayContainer,
  OverlayOutsideClickDispatcher,
  type ConnectedPosition,
  type FlexibleConnectedPositionStrategyOrigin,
  type OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal, type ComponentType } from '@angular/cdk/portal';
import { ScrollDispatcher, ViewportRuler } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { caretEdge, HINT_PANE_CLASS, isWindowPane } from '@lite-workspace/shared/helm-ui/utils';
import type { Subscription } from 'rxjs';

export type HintIcon = 'lock' | 'skip' | 'clock' | 'info';

const TOUCH_DISMISS_MS = 3000;
const DEFAULT_CHIP_MAX_WIDTH_PX = 240;

/** `md` = a status line under a header button: larger full-contrast type and air, icon centred on the whole block. */
export type HintSize = 'sm' | 'md';
const CHIP_SIZE: Record<HintSize, string> = {
  sm: 'items-start gap-1.5 px-2.75 py-1.5 text-xs text-c2',
  md: 'items-center gap-2.5 px-4 py-3 text-sm text-ink',
};

@Component({
  selector: 'hlm-hint-chip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class:
      'pointer-events-none inline-flex w-max rounded-[var(--r)] border border-line2 ' +
      'bg-panel2 font-medium leading-[1.45] shadow-[var(--shadow-3)] [text-wrap:balance]',
    '[class]': 'sizeClass()',
    // Parametric instead of a fixed utility: a caller that knows its copy can widen the cap so the message
    // lands on two dense lines, and `w-max` still shrink-wraps the plate to the longest line it actually has.
    '[style.max-width.px]': 'maxWidth()',
    role: 'tooltip',
  },
  template: `
    @switch (icon()) {
      @case ('lock') {
        <svg
          [attr.viewBox]="'0 0 24 24'"
          fill="none"
          stroke="currentColor"
          stroke-width="1.9"
          stroke-linecap="round"
          stroke-linejoin="round"
          [class]="iconClass()"
          class="size-3.25 flex-none"
          aria-hidden="true"
        >
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
      }
      @case ('skip') {
        <svg
          [attr.viewBox]="'0 0 24 24'"
          fill="none"
          stroke="currentColor"
          stroke-width="1.9"
          stroke-linecap="round"
          stroke-linejoin="round"
          [class]="iconClass()"
          class="size-3.25 flex-none"
          aria-hidden="true"
        >
          <path d="m6 6.5 5.5 5.5L6 17.5" />
          <path d="m13 6.5 5.5 5.5-5.5 5.5" />
        </svg>
      }
      @case ('info') {
        <svg
          [attr.viewBox]="'0 0 24 24'"
          fill="none"
          stroke="currentColor"
          stroke-width="1.9"
          stroke-linecap="round"
          stroke-linejoin="round"
          [class]="iconClass()"
          class="size-[var(--icon-hint)] flex-none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5.5" />
          <path d="M12 7.6h.01" />
        </svg>
      }
      @case ('clock') {
        <svg
          [attr.viewBox]="'0 0 24 24'"
          fill="none"
          stroke="currentColor"
          stroke-width="1.9"
          stroke-linecap="round"
          stroke-linejoin="round"
          [class]="iconClass()"
          class="size-[var(--icon-hint)] flex-none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5V12l3 2" />
        </svg>
      }
    }
    <!-- pre-line so a caller can pin the break point: balancing alone splits mid-phrase. -->
    <span class="whitespace-pre-line">{{ message() }}</span>
  `,
})
export class HlmHintChip {
  readonly message = input<string>('');
  readonly icon = input<HintIcon>('lock');
  readonly maxWidth = input(DEFAULT_CHIP_MAX_WIDTH_PX);
  readonly size = input<HintSize>('sm');
  protected readonly sizeClass = computed(() => CHIP_SIZE[this.size()]);
  /**
   * `sm` sits a muted glyph on the first line; `md` leaves it to `items-center` and keeps the text tone —
   * a muted glyph under full-contrast type all but vanishes on the light panel.
   */
  protected readonly iconClass = computed(() => (this.size() === 'sm' ? 'mt-0.5 text-c3' : ''));
}

export type HintSide = 'top' | 'bottom';
/** Bottom plate edge: `end` hangs from the anchor's far edge (corner icon), `center` from its middle (header button). */
export type HintAlign = 'end' | 'center';

/**
 * Грань карточки с уголком. `left`/`right` — карточка стоит сбоку от якоря в вертикальном ряду иконок.
 * `none` — карточка стоит сбоку от чужого окна, и уголка у неё нет.
 */
export type HintCaret = HintSide | 'left' | 'right' | 'none';

/** One air for every hint in the system: chip and card stand the same distance from the anchor's box. */
const HINT_GAP_PX = 10;

/** Air a hint keeps from the screen edge — the margin the strategy itself works inside. */
const VIEWPORT_INSET_PX = 8;

/**
 * Окно `brn-dialog` реагирует на Escape, только пока его оверлей последний в очереди
 * `OverlayOutsideClickDispatcher` (spartan-ng-brain-dialog.mjs). Плашка или карточка, всплывшая над
 * уже открытым окном, встаёт в конец этой очереди и глушит его Escape — выписываем её оттуда сразу
 * после показа: своих подписок на клик наружу у подсказки нет, терять нечего.
 */
function leaveOverlayArbitration(injector: Injector, overlayRef: OverlayRef): void {
  injector.get(OverlayOutsideClickDispatcher).remove(overlayRef);
}

/**
 * Якорь стоит внутри открытого меню CDK («?» у пункта «Действий»). Корневое меню закрывается целиком
 * и от клика мимо своей панели, и от ухода фокуса из стопки — нажатие по крестику или ролику карточки
 * было для него и тем и другим. Карточка остаётся в очереди `OverlayOutsideClickDispatcher` с пустой
 * подпиской: очередь идёт сверху вниз и на слое, внутри которого клик, останавливается — до меню он
 * не доходит. `mousedown` без действия по умолчанию оставляет фокус в меню, `click` при этом приходит.
 * Escape окна под меню это не отнимает: пока открыто меню, последним в очереди стоит оно само.
 */
function keepAnchorMenuOpen(overlayRef: OverlayRef): void {
  overlayRef.outsidePointerEvents().subscribe();
  overlayRef.overlayElement.addEventListener('mousedown', event => event.preventDefault());
}

// Сверху плашка стоит по центру хозяина: там она подпись к кнопке. Снизу хозяин — значок в углу кадра,
// и плашка равняется по его дальнему краю, чтобы уйти внутрь плитки, а не за её край.
const HINT_POSITIONS: Record<HintSide, ConnectedPosition[]> = {
  top: [
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -HINT_GAP_PX },
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: HINT_GAP_PX },
  ],
  bottom: [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: HINT_GAP_PX },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -HINT_GAP_PX },
  ],
};

const BOTTOM_CENTER_POSITIONS: ConnectedPosition[] = [
  { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: HINT_GAP_PX },
  { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -HINT_GAP_PX },
];

/** Overlay anchored to the origin on the asked side (flips to the mirror when it would clip). */
export function createHintChipOverlay(
  overlay: Overlay,
  origin: ElementRef | HTMLElement,
  side: HintSide = 'top',
  align: HintAlign = 'end',
): OverlayRef {
  return overlay.create({
    positionStrategy: overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(side === 'bottom' && align === 'center' ? BOTTOM_CENTER_POSITIONS : HINT_POSITIONS[side])
      .withViewportMargin(VIEWPORT_INSET_PX)
      .withPush(true),
    scrollStrategy: overlay.scrollStrategies.reposition(),
  });
}

/**
 * How far the anchor's centre stands from the card's near edge once the plate slides sideways: the card
 * corner radius plus half the caret plus a hair of air, so the caret never rides the rounded corner.
 * The card's caret repeats this number from its own edge (`coach-card.component.ts`).
 */
const CARD_CARET_INSET_PX = 28;

/** Horizontal variants of one side, in the order the viewport gets to choose from. */
type CaretAlign = 'center' | 'start' | 'end';
const CARD_ALIGNS: readonly { readonly overlayX: CaretAlign; readonly offsetX: number }[] = [
  { overlayX: 'center', offsetX: 0 },
  { overlayX: 'start', offsetX: -CARD_CARET_INSET_PX },
  { overlayX: 'end', offsetX: CARD_CARET_INSET_PX },
];

// A card is a plate, not a label: it hangs on the anchor's centre and flips to the mirror side when the
// asked one has no room. An anchor near the left or right edge leaves no room for the centred plate —
// there it slides sideways instead of being sliced, keeping the anchor's centre one caret inset from its
// near edge, so the caret still points at the anchor. Cut copy is never an option.
function hintCardPositions(side: HintSide): ConnectedPosition[] {
  // One air for card and chip alike: `HINT_GAP_PX` from the anchor's box to the card's BODY edge.
  // The caret — a 12px square rotated 45° on that edge — hangs in that air, not a term of the maths.
  const offset = HINT_GAP_PX;
  const row = (originY: HintSide, overlayY: HintSide): ConnectedPosition[] =>
    CARD_ALIGNS.map(({ overlayX, offsetX }) => ({
      originX: 'center' as const,
      originY,
      overlayX,
      overlayY,
      offsetX,
      offsetY: overlayY === 'top' ? offset : -offset,
    }));
  const below = row('bottom', 'top');
  const above = row('top', 'bottom');

  return side === 'bottom' ? [...below, ...above] : [...above, ...below];
}

/** Vertical variants of a side place: centred on the anchor, or slid so the anchor sits one caret inset from an edge. */
const CARD_ALIGNS_Y: readonly { readonly overlayY: 'center' | 'top' | 'bottom'; readonly offsetY: number }[] = [
  { overlayY: 'center', offsetY: 0 },
  { overlayY: 'top', offsetY: -CARD_CARET_INSET_PX },
  { overlayY: 'bottom', offsetY: CARD_CARET_INSET_PX },
];

/**
 * Card to the right (`end`) or left (`start`) of the anchor, `HINT_GAP_PX` from its box; `inset` px more
 * when the box to clear is wider than the anchor (the panel around a menu row).
 */
function cardSidePositions(originX: 'start' | 'end', inset = 0): ConnectedPosition[] {
  const gap = HINT_GAP_PX + inset;

  return CARD_ALIGNS_Y.map(({ overlayY, offsetY }) => ({
    originX,
    originY: 'center' as const,
    overlayX: originX === 'end' ? ('start' as const) : ('end' as const),
    overlayY,
    offsetX: originX === 'end' ? gap : -gap,
    offsetY,
  }));
}

/** Panel of the open CDK menu whose row the anchor is; a «?» or any other control inside the menu is not a row. */
function menuPanelOfRow(anchor: HTMLElement): HTMLElement | null {
  if (!anchor.getAttribute('role')?.startsWith('menuitem')) return null;

  return anchor.closest<HTMLElement>('[data-cdk-menu-stack-id]');
}

/**
 * Anchor in a vertical stack of controls (rail or sidebar icon column): its nearest flex ancestor runs as a
 * column with ≥2 visible items, and the anchor's own item there is an icon — no wider than tall. A text column
 * (copy, full-width button, link under it) is not a rail: a card beside its wide link lands over the copy button.
 * Horizontal rows (toolbar, heading with «?», spread bar) keep top/bottom; a grid ancestor lays the anchor out
 * itself, so a flex column above it is not the anchor's stack.
 */
function inVerticalStack(anchor: HTMLElement): boolean {
  for (let item = anchor, el = item.parentElement; el; item = el, el = item.parentElement) {
    const { display, flexDirection } = getComputedStyle(el);
    if (display.endsWith('grid')) return false;
    if (!display.endsWith('flex')) continue;

    const visible = Array.from(el.children).filter(child => getComputedStyle(child).display !== 'none');
    const { width, height } = item.getBoundingClientRect();

    return flexDirection.startsWith('column') && visible.length >= 2 && width <= height;
  }

  return false;
}

/**
 * Side rule of every card. Anchor in a vertical stack → beside it, on the side with more free screen, then
 * the other side; below/above only when neither side fits — a card under a rail icon covers its neighbours
 * and the canvas. A row of an open menu → the same, beside its panel. Any other anchor → the asked side,
 * then its mirror (`hintCardPositions`).
 */
function cardPositionsFor(anchor: HTMLElement, side: HintSide, screenWidth: number): ConnectedPosition[] {
  const vertical = hintCardPositions(side);
  // Row of an open menu: under the row the card lies on the rows below it. It stands beside the whole
  // panel, on the side with more free screen, caret on the row; below/above only when neither side fits.
  const panel = menuPanelOfRow(anchor);
  if (panel) {
    const row = anchor.getBoundingClientRect();
    const box = panel.getBoundingClientRect();
    const beside: ('start' | 'end')[] = screenWidth - box.right >= box.left ? ['end', 'start'] : ['start', 'end'];
    const inset = (originX: 'start' | 'end'): number =>
      Math.max(0, originX === 'end' ? box.right - row.right : row.left - box.left);

    return [...beside.flatMap(originX => cardSidePositions(originX, inset(originX))), ...vertical];
  }

  if (!inVerticalStack(anchor)) return vertical;

  const { left, right } = anchor.getBoundingClientRect();
  const beside = screenWidth - right >= left ? ['end', 'start'] : ['start', 'end'];

  return [...beside.flatMap(originX => cardSidePositions(originX as 'start' | 'end')), ...vertical];
}

/**
 * Окно открылось на том же якоре и встало на место карточки: карточка отходит вбок от САМОГО окна —
 * так видны оба сразу. Низом по низу окна: якорь такого окна сидит у нижнего края экрана, и расти
 * вниз карточке некуда. Справа места нет — уходит влево.
 */
const CARD_ASIDE_POSITIONS: readonly ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: HINT_GAP_PX },
  { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: HINT_GAP_PX },
  { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -HINT_GAP_PX },
  { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -HINT_GAP_PX },
];

const overlaps = (a: DOMRect, b: DOMRect): boolean =>
  b.width > 0 && b.height > 0 && a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/**
 * Чужое окно поверх карточки: сосед по слою оверлеев, который её перекрывает. Свои подсказки не в
 * счёт — ни карточка, ни плашка при наведении окном не являются: подпись места не занимает.
 */
function paneOverCard(injector: Injector, card: HTMLElement, origin: HTMLElement): HTMLElement | null {
  const box = card.getBoundingClientRect();
  const panes = injector.get(OverlayContainer).getContainerElement().querySelectorAll<HTMLElement>('.cdk-overlay-pane');

  return (
    Array.from(panes).find(
      pane =>
        pane !== card &&
        isWindowPane(pane) &&
        // Панель с самим якорем внутри — дом карточки, а не помеха: «?» внутри окна спрашивают о нём же,
        // и карточка обязана стоять на этом «?», а не сбоку от его окна.
        !pane.contains(origin) &&
        overlaps(box, pane.getBoundingClientRect()),
    ) ?? null
  );
}

export interface HintCardOverlay<T> {
  readonly component: ComponentRef<T>;
  destroy(): void;
}

/**
 * Rich card anchored to an element: the overlay layer of helm-ui carrying someone else's component.
 * Side rule: an anchor in a vertical stack of controls gets the card beside it, never under it (`cardPositionsFor`).
 * The card lives outside every `overflow` ancestor, follows its anchor on scroll and resize, and is
 * told which side its caret ended up on — the asked side, or the mirror the viewport forced — and where
 * that caret sits horizontally once a screen edge pushed the plate sideways. A screen too narrow for the
 * plate at any of those places gets it slid back inside by `shiftX` px, which the content moves its caret
 * back by. Content components therefore accept `caretSide`, `caretAlign` and `shiftX` inputs.
 */
export function createHintCardOverlay<T>(
  injector: Injector,
  content: ComponentType<T>,
  origin: ElementRef | HTMLElement,
  side: HintSide = 'bottom',
  /** Место у якоря может занять его собственное окно — тогда карточка встаёт сбоку от него (`CARD_ASIDE_POSITIONS`). */
  stepAside = false,
): HintCardOverlay<T> {
  const overlay = injector.get(Overlay);
  const originEl: HTMLElement = origin instanceof ElementRef ? origin.nativeElement : origin;
  // Якорь живёт внутри прокручиваемой области приложения, а не окна: без её регистрации стратегия
  // слушает только скролл документа, и карточка остаётся стоять, пока кнопка уезжает под шапку.
  const scrollables = injector.get(ScrollDispatcher).getAncestorScrollContainers(origin);
  const cardPositions = cardPositionsFor(originEl, side, injector.get(ViewportRuler).getViewportSize().width);
  const strategyFor = (anchor: FlexibleConnectedPositionStrategyOrigin, positions: readonly ConnectedPosition[]) =>
    overlay
      .position()
      .flexibleConnectedTo(anchor)
      .withPositions([...positions])
      .withScrollableContainers(scrollables)
      .withViewportMargin(VIEWPORT_INSET_PX)
      // The card belongs to its anchor, not to the screen: the anchor scrolls away — the card goes with it.
      // `withPush` would shove the plate back inside the viewport and leave it hanging at the top edge with
      // its caret pointing at nothing; flexible dimensions would squeeze its height as the anchor nears the
      // bottom edge; the locked position keeps the side chosen at open, so scrolling never flips the caret.
      .withPush(false)
      .withFlexibleDimensions(false)
      .withLockedPosition(true);

  let strategy = strategyFor(origin, cardPositions);
  const overlayRef = overlay.create({
    positionStrategy: strategy,
    scrollStrategy: overlay.scrollStrategies.reposition(),
    panelClass: HINT_PANE_CLASS,
  });

  const component = overlayRef.attach(new ComponentPortal(content, null, injector));
  if (originEl.closest('[data-cdk-menu-stack-id]')) keepAnchorMenuOpen(overlayRef);
  else leaveOverlayArbitration(injector, overlayRef);
  // Стоя сбоку от окна, карточка показывала бы уголком на него же — там уголка нет вовсе: на якорь
  // показывает само окно, и второй стрелки экрану не нужно.
  let asidePane: HTMLElement | null = null;
  // `overlayY: 'top'` means the plate starts under the anchor, so its caret sits on its own top edge —
  // the caret side is the mirror of the side the card hangs on, and the flip swaps both together.
  // `overlayX` says the same about the horizontal: the plate slid sideways, so the caret slides with it.
  // A side place (`originX` at the anchor's edge) turns both axes: the caret sits on the card's near side
  // edge, and `overlayY` says where along it.
  const Y_ALIGN: Record<string, CaretAlign> = { center: 'center', top: 'start', bottom: 'end' };
  const tellContent = (place: ConnectedPosition): void => {
    const edge: HintCaret = caretEdge(place);
    const beside = edge === 'left' || edge === 'right';
    const { overlayX, overlayY } = place;
    component.setInput('caretSide', asidePane ? 'none' : edge);
    component.setInput('caretAlign', beside ? Y_ALIGN[overlayY] : overlayX);
  };

  tellContent(cardPositions[0]);

  /**
   * Экран уже, чем нужно карточке с её тремя вариантами по горизонтали: у якоря посреди узкой шапки
   * ни один не встаёт целиком, и стратегия берёт наименее плохой — карточку срезает о край экрана.
   * Задвигаем плиту обратно внутрь и говорим карточке, насколько: уголок уходит на столько же в
   * обратную сторону и остаётся над серединой якоря. Сдвиг живёт на самой карточке внутри панели —
   * коробка панели его не видит и на каждом пересчёте показывает место, выбранное стратегией.
   */
  let shiftX = 0;
  const clampIntoViewport = (): void => {
    if (!overlayRef.hasAttached()) return;

    const box = overlayRef.overlayElement.getBoundingClientRect();
    // jsdom раскладки не считает: коробки нет — двигать нечего.
    if (!box.width) return;

    const screen = injector.get(ViewportRuler).getViewportSize().width;
    const next =
      box.left < VIEWPORT_INSET_PX ? VIEWPORT_INSET_PX - box.left : Math.min(0, screen - VIEWPORT_INSET_PX - box.right);
    if (next === shiftX) return;

    shiftX = next;
    component.setInput('shiftX', next);
  };

  /**
   * Место в скролле и на смене размеров CDK пересчитывает ВНЕ зоны Angular: `setInput` оттуда метит
   * карточку грязной, но тика не будет — посчитанный сдвиг не доезжает до разметки, и плиту видно
   * срезанной о край экрана ровно до следующего чужого события. Возврат в зону — и сдвиг рисуется.
   */
  const zone = injector.get(NgZone);
  const follow = (): Subscription =>
    strategy.positionChanges.subscribe(change =>
      zone.run(() => {
        tellContent(change.connectionPair);
        clampIntoViewport();
      }),
    );
  let positions = follow();
  // Первое место стратегия выбирает ещё до подписки, а мерить карточку можно только отрисованной.
  requestAnimationFrame(() => zone.run(clampIntoViewport));

  /**
   * Якорь уезжает и без скролла: окно догрузило блок и заново встало по центру, окно ещё въезжает
   * анимацией, над якорем вырос соседний блок. CDK пересчитывает место только на скролле и смене
   * размеров экрана — карточка осталась бы там, где якорь был, уголком в пустоту. Якорь и растёт на
   * месте: свёрнутый блок раскрылся, и нижний край якоря уехал вниз от нулевой высоты. Сверяем место и
   * размер якоря каждый кадр и двигаем карточку следом; сторона при этом та же — место заперто.
   */
  let anchorAt = '';
  let frame = 0;
  const trackAnchor = (): void => {
    const { left, top, width, height } = originEl.getBoundingClientRect();
    const at = `${left}:${top}:${width}:${height}`;
    if (anchorAt && at !== anchorAt && overlayRef.hasAttached()) overlayRef.updatePosition();
    anchorAt = at;
    frame = requestAnimationFrame(trackAnchor);
  };

  zone.runOutsideAngular(() => (frame = requestAnimationFrame(trackAnchor)));

  /**
   * Окно, открывшееся на якоре карточки, встаёт поверх неё: место у якоря теперь его, и карточка
   * отходит вбок от самого окна. Пока окно живо, уступленное место назад не отбирается — вернувшись
   * к якорю, карточка снова оказалась бы под ним. Окно ушло из слоя — карточка возвращается.
   */
  const restack = (): void => {
    if (!overlayRef.hasAttached()) return;

    const pane = asidePane?.isConnected ? asidePane : paneOverCard(injector, overlayRef.overlayElement, originEl);
    if (pane === asidePane) return;

    asidePane = pane;
    positions.unsubscribe();
    strategy = pane ? strategyFor(pane, CARD_ASIDE_POSITIONS) : strategyFor(origin, cardPositions);
    overlayRef.updatePositionStrategy(strategy);
    positions = follow();
    overlayRef.updatePosition();
  };

  // Слой меняется в момент вставки панели, когда та ещё не отрисована и коробки у неё нет: второй
  // заход на следующем кадре видит её настоящее место. Повтор без перемен ничего не делает.
  const watchLayer = (): void => {
    restack();
    requestAnimationFrame(restack);
  };

  // Окно могло открыться ДО карточки — её собственный показ слой уже не меняет, и ждать нечего.
  if (stepAside) requestAnimationFrame(restack);
  const stopWatchingLayer = stepAside ? onOverlayLayerChange(injector, watchLayer) : null;

  return {
    component,
    destroy: () => {
      cancelAnimationFrame(frame);
      stopWatchingLayer?.();
      positions.unsubscribe();
      overlayRef.dispose();
    },
  };
}

/**
 * Якорь накрыт чужим модальным окном. CDK держит оверлеи детьми одного контейнера в порядке показа и
 * вставляет подложку окна прямо перед его панелью: всё, что стоит после последней подложки, нарисовано
 * поверх окна. Якорь внутри этого верхнего окна — подсказка его часть и всплывает над ним; якорь под
 * подложкой — подсказка закрыла бы собой окно, и показывать её там нельзя.
 */
export function isAnchorUnderModal(injector: Injector, anchor: HTMLElement): boolean {
  const layers = Array.from(injector.get(OverlayContainer).getContainerElement().children);
  let backdrop = -1;
  layers.forEach((layer, index) => {
    if (layer.classList.contains('cdk-overlay-backdrop')) backdrop = index;
  });

  if (backdrop >= 0 && !layers.some((layer, index) => index > backdrop && layer.contains(anchor))) return true;

  return isAnchorCoveredByPane(anchor);
}

/**
 * Окно без подложки (`hasBackdrop: false`) закрывает кадр собственной непрозрачной панелью: очередь
 * слоёв о нём молчит, и спрашивать приходится саму страницу — что нарисовано в центре якоря.
 */
function isAnchorCoveredByPane(anchor: HTMLElement): boolean {
  // jsdom не считает раскладку: без неё точка всегда пустая, и правило молчит.
  if (typeof document.elementFromPoint !== 'function') return false;

  const rect = anchor.getBoundingClientRect();
  const top = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
  if (!top || anchor.contains(top)) return false;

  const pane = top.closest('.cdk-overlay-pane');
  return pane !== null && !pane.classList.contains(HINT_PANE_CLASS);
}

/** Окно открывается и закрывается вне потока подсказок: следим за самим слоем оверлеев. Возврат — отписка. */
export function onOverlayLayerChange(injector: Injector, run: () => void): () => void {
  const observer = new MutationObserver(run);
  observer.observe(injector.get(OverlayContainer).getContainerElement(), { childList: true });

  return () => observer.disconnect();
}

/**
 * Pill above the host on hover (tap on touch, auto-dismissed after 3s); empty message keeps it inert.
 * Focus never shows it: focus handed back by a dialog or a window refocus lands with no pointer on the host,
 * and no pointerleave ever comes to hide it. Focus leaving still hides.
 * `hlmHintOpen` hands the show to the caller — then hover and focus stay silent.
 */
@Directive({
  selector: '[hlmHint]',
  standalone: true,
  host: {
    '(pointerenter)': 'onPointerEnter($event)',
    '(pointerleave)': 'onPointerLeave($event)',
    '(focusout)': 'onFocusOut()',
  },
})
export class HlmHint implements OnDestroy {
  readonly hlmHint = input<string>('');
  readonly hlmHintIcon = input<HintIcon>('lock');
  /** Потолок ширины плитки: длинная подсказка на 240px вырождается в узкий столбик из семи строк. */
  readonly hlmHintMaxWidth = input(DEFAULT_CHIP_MAX_WIDTH_PX);
  /**
   * Внешнее управление показом. `null` — плашка живёт своим наведением и фокусом. Булево значение
   * отдаёт её хозяину целиком: наведение перестаёт открывать и закрывать, показ идёт только по сигналу.
   * Так подсказку вешают туда, где открывает её клик, а не ховер.
   */
  readonly hlmHintOpen = input<boolean | null>(null);
  /** Сторона показа: сверху — подпись к кнопке, снизу — объяснение под значком в углу кадра. */
  readonly hlmHintSide = input<HintSide>('top');
  /** Снизу: `end` — плашка от дальнего края значка, `center` — по центру кнопки. Сверху она по центру всегда. */
  readonly hlmHintAlign = input<HintAlign>('end');
  readonly hlmHintSize = input<HintSize>('sm');

  private readonly overlay = inject(Overlay);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);

  private overlayRef: OverlayRef | null = null;
  private chipRef: ComponentRef<HlmHintChip> | null = null;
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // show() snapshots the inputs at attach time; without this an open chip keeps a stale gate reason.
    effect(() => {
      const message = this.hlmHint();
      const icon = this.hlmHintIcon();
      const maxWidth = this.hlmHintMaxWidth();
      if (!this.chipRef) {
        return;
      }

      if (!message) {
        this.hide();
        return;
      }

      this.chipRef.setInput('message', message);
      this.chipRef.setInput('icon', icon);
      this.chipRef.setInput('maxWidth', maxWidth);
    });

    effect(() => {
      const open = this.hlmHintOpen();
      if (open === null) return;
      if (open) this.show();
      else this.hide();
    });
  }

  /** Под внешним управлением свои жесты молчат: открывает и закрывает плашку только хозяин. */
  private controlled(): boolean {
    return this.hlmHintOpen() !== null;
  }

  protected onPointerEnter(event: PointerEvent): void {
    if (this.controlled()) return;
    this.show();
    if (event.pointerType === 'touch') this.scheduleDismiss();
  }

  protected onPointerLeave(event: PointerEvent): void {
    if (this.controlled()) return;
    if (event.pointerType !== 'touch') this.hide();
  }

  protected onFocusOut(): void {
    if (!this.controlled()) this.hide();
  }

  protected show(): void {
    const message = this.hlmHint();
    if (!message) {
      this.hide();
      return;
    }

    this.overlayRef ??= createHintChipOverlay(this.overlay, this.elementRef, this.hlmHintSide(), this.hlmHintAlign());
    if (!this.overlayRef.hasAttached()) {
      this.chipRef = this.overlayRef.attach(new ComponentPortal(HlmHintChip, null, this.injector));
      leaveOverlayArbitration(this.injector, this.overlayRef);
    }

    this.chipRef?.setInput('size', this.hlmHintSize());
    this.chipRef?.setInput('message', message);
    this.chipRef?.setInput('icon', this.hlmHintIcon());
    this.chipRef?.setInput('maxWidth', this.hlmHintMaxWidth());
  }

  protected hide(): void {
    this.clearDismiss();
    if (this.overlayRef?.hasAttached()) this.overlayRef.detach();
    this.chipRef = null;
  }

  private scheduleDismiss(): void {
    this.clearDismiss();
    this.dismissTimer = setTimeout(() => this.hide(), TOUCH_DISMISS_MS);
  }

  private clearDismiss(): void {
    if (this.dismissTimer !== null) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearDismiss();
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.chipRef = null;
  }
}
